import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { triggerToast } from '@components/atoms/toast';
import { useLeagueContext } from '@hooks/useLeagueContext';
import {
  usePlannerMutations,
  type TransferVariables
} from '@hooks/my-team/usePlannerMutations';
import { TRANSFER_RECOMMENDATIONS_QUERY_KEY } from '@hooks/useTransferRecommendations';
import { sortTransfersByBudgetImpact } from '@lib/my-team/utils/sort-transfers';
import { deriveTransferHit } from '@lib/my-team/utils/transfer-hit';
import { PlannerApiError } from '@api-service/planner';
import type { TransferRecommendation } from '@api-service/recommendations';

type UseMakeTransfersOptions = {
  transfers: TransferRecommendation[];
  /**
   * Free transfers for the upcoming gameweek; null when unlimited (wildcard or
   * free hit). Drives the points-hit warning before the commit.
   */
  freeTransfers: number | null;
  /** Transfers already made this gameweek, which the suggested ones add to. */
  transfersAlreadyMade?: number;
  /**
   * Clears whichever store drove these suggestions. Runs once the redirect has
   * completed, so the tab that requested the commit is already unmounted.
   */
  onCommitted: () => void;
};

const GENERIC_ERROR_MESSAGE =
  'Something went wrong making your transfers. Please try again.';

/**
 * Commits a set of AI transfer suggestions to the planner. Shared by both the
 * recommended and custom tabs, which differ only in the store they reset once
 * the commit lands.
 */
export const useMakeTransfers = ({
  transfers,
  freeTransfers,
  transfersAlreadyMade = 0,
  onCommitted
}: UseMakeTransfersOptions) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { context } = useLeagueContext();
  const { commitTransfers } = usePlannerMutations();

  // The suggestions the warning described, frozen while it is on screen. The
  // recommendations query can refetch underneath an open modal, so committing
  // the live list would post a different set from the one that was priced.
  const [pendingHitEvents, setPendingHitEvents] = useState<
    TransferVariables[] | null
  >(null);

  const transferHit = deriveTransferHit(
    freeTransfers,
    transfersAlreadyMade + transfers.length
  );

  /**
   * Budget-freeing transfers go first so the bank never dips negative while the
   * backend replays the batch sequentially. Mirrors the pick flow.
   */
  const buildEvents = (gameweek: number): TransferVariables[] =>
    sortTransfersByBudgetImpact(
      transfers.map((transfer) => ({
        outgoingPlayerId: transfer.out.id,
        incomingPlayerId: transfer.in.id,
        outgoingPrice: transfer.out.price,
        incomingPrice: transfer.in.price
      }))
    ).map(({ outgoingPlayerId, incomingPlayerId }) => ({
      gameweek,
      outgoingPlayerId,
      incomingPlayerId
    }));

  const makeTransfers = () => {
    const gameweek = context?.nextGameweek?.number;
    // Everything the commit needs is checked here, before the member is asked
    // to accept a points hit: being told the cost and then handed a generic
    // failure is worse than failing outright.
    if (!gameweek || transfers.length === 0) {
      triggerToast({
        title: 'Error',
        subTitle: GENERIC_ERROR_MESSAGE,
        variant: 'error'
      });
      return;
    }

    const events = buildEvents(gameweek);

    // Warn before committing a set that exceeds the free allowance, so the
    // points cost is never a surprise at the deadline (FFH-2998).
    if (transferHit.exceedsFree) {
      setPendingHitEvents(events);
      return;
    }

    commitSuggestedTransfers(events);
  };

  const commitSuggestedTransfers = (events: TransferVariables[]) => {
    commitTransfers.mutate(
      { transfers: events },
      {
        onSuccess: async () => {
          triggerToast({
            title: 'Success',
            subTitle: `Transfer${events.length > 1 ? 's' : ''} made`,
            variant: 'success'
          });
          // Store reset and cache removal wait for the redirect: while the
          // calling page still observes the recommendations query, resetting
          // the store changes its key and removing it recreates it, either of
          // which fires a premature solver request. Clearing the cache once
          // unmounted means a return visit loads from scratch instead of
          // flashing the pre-commit list while the query waits for an
          // access token.
          setPendingHitEvents(null);
          await router.push('/my-team/pick');
          onCommitted();
          queryClient.removeQueries({
            queryKey: TRANSFER_RECOMMENDATIONS_QUERY_KEY
          });
        },
        onError: (error) => {
          setPendingHitEvents(null);
          triggerToast({
            title: 'Error',
            subTitle:
              error instanceof PlannerApiError
                ? error.message
                : GENERIC_ERROR_MESSAGE,
            variant: 'error'
          });
        }
      }
    );
  };

  // Commits exactly the set the warning priced, not whatever the query holds
  // by the time the member accepts it.
  const confirmHit = () => {
    if (pendingHitEvents) commitSuggestedTransfers(pendingHitEvents);
  };

  const dismissHit = () => setPendingHitEvents(null);

  return {
    makeTransfers,
    isMakingTransfers: commitTransfers.isLoading,
    transferHit,
    isHitWarningOpen: pendingHitEvents !== null,
    confirmHit,
    dismissHit
  };
};
